import type { ReadinessLocale } from './readinessLocale';
import type { GlossaryTerm } from '../constants/glossaryTerms';

export type GlossaryLocaleEntry = Pick<GlossaryTerm, 'term' | 'plain' | 'example'>;

/** Hindi plain-language glossary overrides (English falls back to glossaryTerms.ts). */
export const GLOSSARY_LOCALE_ENTRIES: Record<
  Exclude<ReadinessLocale, 'en'>,
  Partial<Record<string, GlossaryLocaleEntry>>
> = {
  hi: {
    gst: {
      term: 'GST',
      plain: 'वस्तु एवं सेवा कर — आपका 15 अक्षरों का कर पंजीकरण नंबर, जो बिलों पर लिखा होता है।',
    },
    pan: {
      term: 'PAN',
      plain: 'स्थायी खाता संख्या — आयकर विभाग द्वारा जारी 10 अक्षरों का पहचान नंबर।',
    },
    udyam: {
      term: 'उद्यम',
      plain: 'उद्यम पोर्टल से मिलने वाला MSME पंजीकरण नंबर — छोटे व्यवसाय की आधिकारिक पहचान।',
    },
    brsr: {
      term: 'BRSR',
      plain: 'व्यापार जिम्मेदारी और स्थिरता रिपोर्ट — कुछ भारतीय कंपनियों को SEBI को सालाना देनी होती है।',
    },
    cbam: {
      term: 'CBAM',
      plain: 'EU कार्बन बॉर्डर नियम — यूरोप निर्यात पर अतिरिक्त कार्बन जानकारी चाहिए।',
    },
    scope1: {
      term: 'स्कोप 1',
      plain: 'आपकी फैक्ट्री या दुकान में जलाए गए ईंधन से निकलने वाला उत्सर्जन — जेनरेटर, भट्ठी, गाड़ियाँ।',
      example: 'जेनरेटर का डीज़ल या उत्पादन में LPG यहाँ गिना जाता है।',
    },
    scope_1: {
      term: 'स्कोप 1 (प्रत्यक्ष)',
      plain: 'आपकी फैक्ट्री या दुकान में जलाए गए ईंधन से निकलने वाला उत्सर्जन — जेनरेटर, भट्ठी, गाड़ियाँ।',
      example: 'जेनरेटर का डीज़ल या उत्पादन में LPG यहाँ गिना जाता है।',
    },
    scope2: {
      term: 'स्कोप 2',
      plain: 'ग्रिड से खरीदी बिजली से होने वाला उत्सर्जन।',
      example: 'MSEB या राज्य बिजली बोर्ड का बिल मुख्य रूप से स्कोप 2 बनाता है।',
    },
    scope_2: {
      term: 'स्कोप 2 (बिजली)',
      plain: 'ग्रिड से खरीदी बिजली से होने वाला उत्सर्जन।',
      example: 'MSEB या राज्य बिजली बोर्ड का बिल मुख्य रूप से स्कोप 2 बनाता है।',
    },
    scope3: {
      term: 'स्कोप 3',
      plain: 'आपकी सप्लाई चेन से जुड़ा अप्रत्यक्ष उत्सर्जन — कच्चा माल, परिवहन, कचरा।',
      example: 'स्टील खरीद, कूरियर डिलीवरी और कचरा निपटान अक्सर यहाँ आते हैं।',
    },
    scope_3: {
      term: 'स्कोप 3 (वैल्यू चेन)',
      plain: 'आपकी सप्लाई चेन से जुड़ा अप्रत्यक्ष उत्सर्जन — कच्चा माल, परिवहन, कचरा।',
      example: 'स्टील खरीद, कूरियर डिलीवरी और कचरा निपटान अक्सर यहाँ आते हैं।',
    },
    tco2e: {
      term: 'tCO₂e',
      plain: 'कार्बन डाइऑक्साइड समतुल्य टन — आपके कुल ग्रीनहाउस गैस पदचिह्न को मापने का मानक तरीका।',
    },
    carbon_footprint: {
      term: 'कार्बन पदचिह्न',
      plain: 'आपके व्यवसाय की गतिविधियों से निकलने वाली कुल ग्रीनहाउस गैस, आमतौर पर एक साल में।',
    },
    carbon_score: {
      term: 'कार्बन स्कोर',
      plain: '0–100 की सरल रेटिंग — आपका उत्सर्जन प्रबंधन आपके क्षेत्र के औसत से कैसा है।',
    },
    ghg: {
      term: 'GHG',
      plain: 'ग्रीनहाउस गैसें — CO₂ जैसी गैसें जो गर्मी रोकती हैं और जलवायु बदलाव में योगदान देती हैं।',
    },
    msme: {
      term: 'MSME',
      plain: 'सूक्ष्म, लघु और मध्यम उद्यम — भारत में छोटे और मध्यम व्यवसायों की आधिकारिक श्रेणी।',
    },
    ocr: {
      term: 'स्वचालित बिल पढ़ना',
      plain: 'ऐप स्कैन किए बिलों से अपने आप राशि और तारीख निकाल लेता है — हाथ से टाइप नहीं करना पड़ता।',
      example: 'बिजली बिल की फोटो अपलोड करें; हम kWh, राशि और तारीख निकाल लेंगे।',
    },
    emission_factor: {
      term: 'उत्सर्जन गुणांक',
      plain: 'एक मानक रूपांतरण दर — kWh या डीज़ल लीटर जैसे डेटा को CO₂ उत्सर्जन में बदलती है।',
    },
  },
};

export const WORKFLOW_GUIDE_LOCALE = {
  en: {
    title: 'How it works — 4 simple steps',
    subtitlePrefix: 'No ESG jargon required. Upload bills or connect Tally/Zoho, see your',
    subtitleSuffix: ', then share reports with buyers or banks.',
    hide: 'Hide',
    show: 'Show steps',
    goToStep: 'Go to step',
    closeExplanation: 'Close explanation',
    examplePrefix: 'Example:',
    tapHint: 'Tap for plain-language explanation with picture.',
  },
  hi: {
    title: 'कैसे काम करता है — 4 आसान कदम',
    subtitlePrefix: 'ESG शब्दजाल की ज़रूरत नहीं। बिल अपलोड करें या Tally/Zoho जोड़ें, अपना',
    subtitleSuffix: ' देखें, फिर खरीदार या बैंक के साथ रिपोर्ट साझा करें।',
    hide: 'छिपाएँ',
    show: 'कदम दिखाएँ',
    goToStep: 'इस कदम पर जाएँ',
    closeExplanation: 'व्याख्या बंद करें',
    examplePrefix: 'उदाहरण:',
    tapHint: 'सरल भाषा और चित्र के लिए टैप करें।',
  },
} as const satisfies Record<ReadinessLocale, Record<string, string>>;

export const SCOPE_EXPLAINER_STRIP_LOCALE = {
  en: {
    title: 'What do Scope 1, 2 & 3 mean?',
    subtitle: 'Each picture shows where emissions come from. Tap a scope name for a plain-language explanation.',
    subtitleWithEmissions:
      'Your emissions sit on each picture — Scope 1 (direct), Scope 2 (electricity), Scope 3 (value chain). Tap a name for more detail.',
    yourTotal: 'Your total',
    emissionsLabel: 'Your emissions',
    yourEmissions: 'Your emissions',
    ofTotal: 'of total',
    noDataYet: 'No data yet',
    examplePrefix: 'Example:',
  },
  hi: {
    title: 'स्कोप 1, 2 और 3 का क्या मतलब है?',
    subtitle: 'हर चित्र बताता है उत्सर्जन कहाँ से आता है। सरल भाषा के लिए स्कोप नाम पर टैप करें।',
    subtitleWithEmissions:
      'आपके उत्सर्जन हर चित्र पर दिखते हैं — स्कोप 1 (प्रत्यक्ष), स्कोप 2 (बिजली), स्कोप 3 (वैल्यू चेन)। और जानने के लिए नाम पर टैप करें।',
    yourTotal: 'आपका कुल',
    emissionsLabel: 'आपके उत्सर्जन',
    yourEmissions: 'आपके उत्सर्जन',
    ofTotal: 'कुल का',
    noDataYet: 'अभी डेटा नहीं',
    examplePrefix: 'उदाहरण:',
  },
} as const satisfies Record<
  ReadinessLocale,
  {
    title: string;
    subtitle: string;
    subtitleWithEmissions: string;
    yourTotal: string;
    emissionsLabel: string;
    yourEmissions: string;
    ofTotal: string;
    noDataYet: string;
    examplePrefix: string;
  }
>;

export const POST_LOGIN_WORKFLOW_LOCALE = {
  en: [
    { title: 'Register & profile', description: 'Sector, operations, and emission drivers.' },
    { title: 'Connect data', description: 'Upload bills or sync accounting exports.' },
    { title: 'Carbon assessment', description: 'Baseline scopes and reduction opportunities.' },
    { title: 'Report & improve', description: 'Dashboard, compliance hub, and cycles.' },
  ],
  hi: [
    { title: 'पंजीकरण और प्रोफ़ाइल', description: 'क्षेत्र, संचालन और उत्सर्जन स्रोत।' },
    { title: 'डेटा जोड़ें', description: 'बिल अपलोड करें या Tally/Zoho से जोड़ें।' },
    { title: 'कार्बन आकलन', description: 'स्कोप बेसलाइन और कमी के अवसर।' },
    { title: 'रिपोर्ट और सुधार', description: 'डैशबोर्ड, compliance hub और चक्र।' },
  ],
} as const;

export const GLOSSARY_ILLUSTRATION_ALT_LOCALE: Record<
  ReadinessLocale,
  Partial<Record<string, string>>
> = {
  en: {},
  hi: {
    scope_direct: 'फैक्ट्री या गाड़ियों में जलाए ईंधन से प्रत्यक्ष उत्सर्जन।',
    scope_electricity: 'ग्रिड से खरीदी बिजली से उत्सर्जन।',
    scope_supply_chain: 'कच्चा माल, परिवहन और सप्लायर्स से अप्रत्यक्ष उत्सर्जन।',
    documents: 'बिल और चालान अपलोड करके स्वचालित पढ़ने का चित्र।',
    carbon_assessment: 'श्रेणी-wise कार्बन पदचिह्न आकलन का चित्र।',
    reporting: 'खरीदार और बैंक के लिए compliance रिपोर्ट का चित्र।',
    dashboard: 'कार्बन स्कोर और रुझान दिखाने वाला डैशबोर्ड।',
    finance: 'कार्बन प्रदर्शन से हरित ऋण और योजनाओं का लिंक।',
    workflow: 'माप–कार्रवाई–रिपोर्ट वर्कफ़्लो लूप का चित्र।',
  },
};

export const getGlossaryLocaleEntry = (
  termId: string,
  locale: ReadinessLocale
): GlossaryLocaleEntry | undefined => {
  if (locale === 'en') {
    return undefined;
  }
  return GLOSSARY_LOCALE_ENTRIES[locale]?.[termId];
};
